// InputUploader — drag-and-drop / click-to-upload component for the
// Smart Intake "Describe the project" step.
//
// Two roles in one widget, controlled by ``kind``:
//
//   * ``content`` → supporting documents the Observer (Phase 2) will
//     read to extract constraints. Specs, vendor PDFs, schemas, etc.
//   * ``format``  → formatting / style instructions the Exporter
//     (Phase 5) should imitate. Sample requirement docs, brand
//     guidelines, RFP templates, etc.
//
// On Step 1 of the wizard we *can't* upload yet because the project
// hasn't been created. So this component supports two modes:
//
//   1. **Pending mode** (no projectId yet): we hold ``File`` objects
//      in local state and surface them as removable chips. The parent
//      `SmartIntakePage` reads ``files`` via the ``onChange`` handler
//      and POSTs them after `createProject` succeeds.
//   2. **Live mode** (projectId provided): we POST each upload
//      immediately to ``/api/projects/{id}/inputs`` and show the
//      server-side InputRow it returns.
//
// Live mode is what the Command Center inspector uses so the user can
// keep adding/removing inputs after the project exists.

import { useId, useRef, useState } from 'react'
import {
  AlertCircle,
  FileText,
  Loader2,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react'

import { api, ApiError } from '@/lib/api'
import { humanBytes } from '@/lib/format'
import { toast } from '@/hooks/use-toast'
import type { InputRow } from '@/types'

export type UploadKind = 'content' | 'format'

export interface PendingFile {
  /** A stable client-side id so React keys are happy. */
  id: string
  file: File
}

interface BaseProps {
  kind: UploadKind
  /** Heading & explainer for the dropzone. */
  title: string
  description: string
  /** Used for the helper text under the empty dropzone. */
  hint?: string
}

interface PendingProps extends BaseProps {
  mode: 'pending'
  files: PendingFile[]
  onChange: (next: PendingFile[]) => void
  /** Pending-mode never has a projectId — kept undefined for type-safety. */
  projectId?: undefined
}

interface LiveProps extends BaseProps {
  mode: 'live'
  projectId: string
  /** Already-saved inputs, fetched from the server. */
  rows: InputRow[]
  /** Refetch trigger after a successful upload / delete. */
  onChanged: () => void
}

type Props = PendingProps | LiveProps

const ACCEPT_EXTENSIONS = [
  '.md',
  '.markdown',
  '.txt',
  '.rtf',
  '.docx',
  '.pdf',
  '.pptx',
  '.xlsx',
  '.html',
  '.htm',
  '.json',
  '.yaml',
  '.yml',
  '.csv',
  '.tsv',
  '.png',
  '.jpg',
  '.jpeg',
  '.svg',
]

function makeId(): string {
  // crypto.randomUUID is widely available; fall back to Date.now if not.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * Validate a File against the allowed-extension list. Returns null on
 * success or a human-friendly error message otherwise. We lean on the
 * extension because MIME types are unreliable cross-browser.
 */
function validateFile(file: File): string | null {
  const lower = file.name.toLowerCase()
  if (!ACCEPT_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return `"${file.name}" — unsupported type (allowed: ${ACCEPT_EXTENSIONS.join(', ')})`
  }
  if (file.size <= 0) {
    return `"${file.name}" — file is empty.`
  }
  // 25 MB soft cap so a 200 MB PDF doesn't choke the browser. The
  // backend has its own checks; this is just an early-warning bell.
  if (file.size > 25 * 1024 * 1024) {
    return `"${file.name}" — file is larger than 25 MiB.`
  }
  return null
}

export function InputUploader(props: Props) {
  const { kind, title, description, hint } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const dropZoneId = useId()
  const [dragOver, setDragOver] = useState(false)
  const [errorMsgs, setErrorMsgs] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  // PR-I — per-row "refine in flight" tracking so the architect
  // gets a spinner while the synchronous endpoint runs (up to 5 min
  // per plan §12-Q3).
  const [refining, setRefining] = useState<Set<string>>(new Set())

  async function handleRefine(inputId: string, label: string): Promise<void> {
    if (props.mode !== 'live') return  // refine only makes sense post-create
    if (refining.has(inputId)) return
    setRefining((prev) => new Set(prev).add(inputId))
    try {
      const result = await api.refineInput(props.projectId, inputId)
      if (result.refined_text === result.original_text) {
        toast({
          variant: 'success',
          title: `No refinement needed — ${label}`,
          description: (
            'The Import Refiner walked the document and either found '
            + 'no shall clauses to refine OR the refiner is disabled '
            + '(INCOSE_PREVENTION + IMPORT_REFINER flags). See '
            + 'phase0/import-refinements.json if a refinement row '
            + 'was recorded.'
          ),
        })
      } else {
        const counts = [
          `${result.violations_resolved} violations resolved`,
          `${result.repaired_attempts} retries`,
          result.needs_refinement
            ? `${result.missing_fact_diagnostics.length} flagged for review`
            : 'fully clean',
        ].join(' · ')
        toast({
          variant: 'success',
          title: `Refined ${label}`,
          description: (
            counts
            + '. Audit row appended to phase0/import-refinements.json. '
            + 'The refined text is on the response — open DevTools to inspect.'
          ),
        })
        // Surface the full result to the console so the architect can
        // grab the refined text for now. A side-by-side diff modal
        // is a follow-on UI improvement.
        // eslint-disable-next-line no-console
        console.info('Import Refiner result for', label, result)
      }
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : (err as Error).message
      toast({
        variant: 'destructive',
        title: `Refine failed — ${label}`,
        description: msg,
      })
    } finally {
      setRefining((prev) => {
        const next = new Set(prev)
        next.delete(inputId)
        return next
      })
    }
  }

  // -------------------------------------------------------------------- //
  // File ingestion (shared by drop + click + change events)
  // -------------------------------------------------------------------- //
  async function ingest(fileList: FileList | File[]) {
    const incoming = Array.from(fileList)
    if (incoming.length === 0) return

    const errors: string[] = []
    const valid: File[] = []
    for (const f of incoming) {
      const err = validateFile(f)
      if (err) errors.push(err)
      else valid.push(f)
    }

    if (props.mode === 'pending') {
      // Local-only ingestion; we don't talk to the server until the
      // wizard creates the project on submit.
      const additions: PendingFile[] = valid.map((file) => ({
        id: makeId(),
        file,
      }))
      props.onChange([...props.files, ...additions])
      setErrorMsgs(errors)
      return
    }

    // Live mode — upload to the server one-by-one so partial successes
    // are visible if a single file blows up the parser.
    setBusy(true)
    setErrorMsgs([])
    const liveErrors = [...errors]
    for (const file of valid) {
      try {
        await api.uploadInput(props.projectId, kind, file)
      } catch (err) {
        const msg =
          err instanceof ApiError ? err.message : (err as Error).message
        liveErrors.push(`"${file.name}" — ${msg}`)
      }
    }
    props.onChanged()
    setErrorMsgs(liveErrors)
    setBusy(false)
  }

  // -------------------------------------------------------------------- //
  // Drag & drop handlers
  // -------------------------------------------------------------------- //
  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(true)
  }
  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
  }
  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files?.length) ingest(e.dataTransfer.files)
  }

  // -------------------------------------------------------------------- //
  // Row removal handlers
  // -------------------------------------------------------------------- //
  function removePending(id: string) {
    if (props.mode !== 'pending') return
    props.onChange(props.files.filter((f) => f.id !== id))
  }

  async function removeLive(inputId: string) {
    if (props.mode !== 'live') return
    setBusy(true)
    try {
      await api.deleteInput(props.projectId, inputId)
      props.onChanged()
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      setErrorMsgs([msg])
    } finally {
      setBusy(false)
    }
  }

  // -------------------------------------------------------------------- //
  // Render
  // -------------------------------------------------------------------- //
  const isPending = props.mode === 'pending'
  const rowCount = isPending ? props.files.length : props.rows.length

  return (
    <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-4">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div>
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
            {title}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{description}</div>
        </div>
        <span className="text-[11px] text-slate-500 tabular-nums">
          {rowCount} file{rowCount === 1 ? '' : 's'}
        </span>
      </div>

      {/* Drop zone */}
      <div
        id={dropZoneId}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            inputRef.current?.click()
          }
        }}
        className={`mt-2 cursor-pointer rounded-md border-2 border-dashed p-4 text-center transition-colors ${
          dragOver
            ? 'border-indigo-400 bg-indigo-500/10'
            : 'border-slate-700 hover:border-slate-500 bg-slate-950/40'
        }`}
        aria-label={`${title} drop zone — click or drag files here`}
      >
        <Upload className="mx-auto h-5 w-5 text-slate-400" aria-hidden />
        <div className="text-xs text-slate-300 mt-1.5">
          {busy ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
            </span>
          ) : (
            <>
              <strong className="text-indigo-300">Click to upload</strong> or
              drag &amp; drop
            </>
          )}
        </div>
        <div className="text-[10.5px] text-slate-500 mt-1">
          {hint || `Accepted: ${ACCEPT_EXTENSIONS.slice(0, 8).join(', ')} …`}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_EXTENSIONS.join(',')}
          className="hidden"
          onChange={(e) => {
            if (e.target.files) ingest(e.target.files)
            // Reset so re-selecting the same file fires the change event.
            e.target.value = ''
          }}
        />
      </div>

      {/* Error rail */}
      {errorMsgs.length > 0 && (
        <ul className="mt-2 space-y-1">
          {errorMsgs.map((m, i) => (
            <li
              key={i}
              className="flex items-start gap-1.5 text-[11px] text-red-300 bg-red-950/30 border border-red-700/40 rounded px-2 py-1"
            >
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" /> {m}
            </li>
          ))}
        </ul>
      )}

      {/* Saved rows */}
      {rowCount > 0 && (
        <ul className="mt-3 space-y-1.5">
          {isPending
            ? props.files.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center gap-2 rounded-md border border-slate-700/50 bg-slate-900/60 px-2.5 py-1.5"
                >
                  <FileText
                    className="h-3.5 w-3.5 text-indigo-300 shrink-0"
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] text-slate-200 truncate">
                      {row.file.name}
                    </div>
                    <div className="text-[10.5px] text-slate-500 font-mono tabular-nums">
                      {humanBytes(row.file.size)} · pending upload
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      removePending(row.id)
                    }}
                    className="rounded p-1 text-slate-500 hover:text-red-300 hover:bg-red-950/40"
                    aria-label={`Remove ${row.file.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))
            : props.rows.map((row) => (
                <li
                  key={row.input_id}
                  className="flex items-center gap-2 rounded-md border border-slate-700/50 bg-slate-900/60 px-2.5 py-1.5"
                >
                  <FileText
                    className="h-3.5 w-3.5 text-indigo-300 shrink-0"
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] text-slate-200 truncate">
                      {row.label}
                    </div>
                    <div className="text-[10.5px] text-slate-500 font-mono tabular-nums">
                      {humanBytes(row.size_bytes)} ·{' '}
                      {new Date(row.uploaded_at).toLocaleString()}
                    </div>
                  </div>
                  {/* PR-I — Import Refiner trigger. Only meaningful
                      for ``content`` and ``text`` inputs (the
                      backend rejects ``format`` with a 422). The
                      button no-ops gracefully when the
                      ``IMPORT_REFINER_ENABLED`` flag is off — the
                      toast tells the architect to check the audit. */}
                  {(row.kind === 'content' || row.kind === 'text') && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRefine(row.input_id, row.label)
                      }}
                      disabled={refining.has(row.input_id) || busy}
                      className="rounded p-1 text-slate-500 hover:text-amber-300 hover:bg-amber-950/40 disabled:opacity-40"
                      aria-label={`Refine ${row.label} for INCOSE compliance`}
                      title="Refine for INCOSE compliance"
                      data-testid={`refine-input-${row.input_id}`}
                    >
                      {refining.has(row.input_id) ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeLive(row.input_id)
                    }}
                    disabled={busy}
                    className="rounded p-1 text-slate-500 hover:text-red-300 hover:bg-red-950/40 disabled:opacity-40"
                    aria-label={`Remove ${row.label}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
        </ul>
      )}
    </div>
  )
}
